import { Loader } from 'kiswe-ui'
import { KeyDict } from '@/types'
import { AssetType } from '@/types/assets'
import { BackdropMode, isAssetTypeVideoOrText, LayoutBoxField, LayoutBoxType } from '@/types/layouts'
import type { BoxStyling } from './BoxStyling'
import { BoxStylingBase } from './BoxStylingBase'
import { BoxStylingSimpleCg } from './BoxStylingSimpleCg'
import { CAST_PROPERTY_BOX_NAMES } from '../types'

export default class LayoutBox {
  active?: boolean
  autoActive?: boolean
  autoMute?: boolean
  backdropMode: BackdropMode = BackdropMode.None
  boundContentSceneId?: string|null = null
  boxes?: KeyDict<Omit<LayoutBox, 'boxes'>>
  cascade?: boolean
  dedicated?: boolean
  field?: LayoutBoxField
  id: string = ''
  invisible?: boolean
  name: string = ''
  parent?: string
  status?: boolean
  stretchContent?: boolean
  stretchVertical?: boolean
  type: LayoutBoxType = LayoutBoxType.BOX
  types?: AssetType[] = [AssetType.Graphic]
  zindex: number = 900
  // DEPRECATED (?)
  'horizontal-align'?: string = ''
  'vertical-align'?: string = ''
  styling: BoxStyling = new BoxStylingBase()

  // eslint-disable-next-line max-lines-per-function, complexity
  constructor (data?: unknown) {
    if (data === undefined) return
    const active = Loader.loadOptionalBoolean(data, 'active')
    if (active !== undefined) this.active = active
    const autoActive = Loader.loadOptionalBoolean(data, 'autoActive')
    if (autoActive !== undefined) this.autoActive = autoActive
    const autoMute = Loader.loadOptionalBoolean(data, 'autoMute')
    if (autoMute !== undefined) this.autoMute = autoMute
    this.backdropMode = Loader.loadEnum(data, 'backdropMode', BackdropMode.None, Object.values(BackdropMode))
    this.boundContentSceneId = Loader.loadStringOrNull(data, 'boundContentSceneId', this.boundContentSceneId)
    this.boxes = Loader.loadKeyDictOrEmpty(data, 'boxes', (data: unknown) => new LayoutBox(data), {})
    const cascade = Loader.loadOptionalBoolean(data, 'cascade')
    if (cascade !== undefined) this.cascade = cascade
    const dedicated = Loader.loadOptionalBoolean(data, 'dedicated')
    if (dedicated !== undefined) this.dedicated = dedicated
    const field = Loader.loadOptionalEnum(data, 'field', Object.values(LayoutBoxField))
    if (field !== undefined) this.field = field
    this.id = Loader.loadString(data, 'id', this.id)
    const invisible = Loader.loadOptionalBoolean(data, 'invisible')
    if (invisible !== undefined) this.invisible = invisible
    this.name = Loader.loadString(data, 'name', this.name)
    const parent = Loader.loadOptionalString(data, 'parent')
    if (parent !== undefined) this.parent = parent
    const status = Loader.loadOptionalBoolean(data, 'status')
    if (status !== undefined) this.status = status
    const stretchContent = Loader.loadOptionalBoolean(data, 'stretchContent')
    if (stretchContent !== undefined) this.stretchContent = stretchContent
    const stretchVertical = Loader.loadOptionalBoolean(data, 'stretchVertical')
    if (stretchVertical !== undefined) this.stretchVertical = stretchVertical
    this.type = Loader.loadEnum(data, 'type', LayoutBoxType.BOX, Object.values(LayoutBoxType))
    const types = Loader.loadOptionalArray<AssetType>(data, 'types')
    if (types !== undefined) this.types = types
    this.zindex = Loader.loadNumber(data, 'zindex', this.zindex)

    if (typeof data === 'object' && data !== null && 'styling' in data) {
      this.styling = Loader.loadClass<BoxStyling>(data, 'styling', (data: unknown) => {
        return isAssetTypeVideoOrText(this.types) ? new BoxStylingSimpleCg(data) : new BoxStylingBase(data)
      })
    } else {
      this.styling = BoxStylingSimpleCg.getBoxStylingFromLegacyLayoutBox(data, this.types)
    }

    this.styling.zIndex = this.zindex
  }

  get isTaggedBox (): boolean {
    return CAST_PROPERTY_BOX_NAMES.includes(this.name)
  }
}
